import apiClient from './api-client';
import { apiConfig } from '@/config/api';
import { launchConfigService } from './launch-config.service';

interface RefreshResponse {
  status: string;
  message: string;
  duration_seconds: number;
  details: {
    [key: string]: {
      status: string;
      rows_updated?: number;
      error?: string;
    };
  };
}

export const exportService = {
  // Export loans to Excel
  async exportLoansToExcel(options?: {
    limit?: number;
    include_properties?: boolean;
  }): Promise<Blob> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.include_properties)
      params.append('include_properties', 'true');

    const url = `${apiConfig.endpoints.exports.loansExcel}${params.toString() ? '?' + params.toString() : ''}`;

    const response = await apiClient.get(url, {
      responseType: 'blob',
      headers: {
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });

    // Validate Excel response
    this.validateExcelResponse(response);
    return response.data;
  },

  // Export fair value report to Excel (supports report_type)
  async exportFairValueReportToExcel(options?: {
    loan_id?: string | number;
    report_type?: 'standard' | 'commentary' | 'both';
  }): Promise<Blob> {
    const params = new URLSearchParams();
    if (options?.loan_id) params.append('loan_id', options.loan_id.toString());
    if (options?.report_type) params.append('report_type', options.report_type);

    const url = `${apiConfig.endpoints.exports.fairValueReportExcel}${
      params.toString() ? '?' + params.toString() : ''
    }`;

    const response = await apiClient.get(url, {
      responseType: 'blob',
      headers: {
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });

    this.validateExcelResponse(response);
    return response.data;
  },

  // Export pricing results to Excel (~100ms response time) - RECOMMENDED
  async exportPricingResultsToExcel(options?: {
    include_risk_metrics?: boolean;
    include_market_data?: boolean;
  }): Promise<Blob> {
    const params = new URLSearchParams();
    if (options?.include_risk_metrics)
      params.append('include_risk_metrics', 'true');
    if (options?.include_market_data)
      params.append('include_market_data', 'true');

    const url = `${apiConfig.endpoints.exports.pricingResultsExcel}${params.toString() ? '?' + params.toString() : ''}`;

    const response = await apiClient.get(url, {
      responseType: 'blob',
      headers: {
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });

    this.validateExcelResponse(response);
    return response.data;
  },

  // Export complete report to Excel (~3s response time)
  async exportCompleteReportToExcel(options?: {
    limit?: number;
  }): Promise<Blob> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());

    const url = `${apiConfig.endpoints.exports.completeReportExcel}${params.toString() ? '?' + params.toString() : ''}`;

    const response = await apiClient.get(url, {
      responseType: 'blob',
      timeout: 10000, // 10 seconds for larger report
      headers: {
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });

    this.validateExcelResponse(response);
    return response.data;
  },

  // Export portfolio analysis to Excel (NEW)
  async exportPortfolioAnalysisToExcel(): Promise<Blob> {
    const response = await apiClient.get(
      apiConfig.endpoints.exports.portfolioAnalysisExcel,
      {
        responseType: 'blob',
        headers: {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      }
    );

    this.validateExcelResponse(response);
    return response.data;
  },

  // Export portfolio summary report to Excel (NEW)
  async exportPortfolioSummaryReportToExcel(): Promise<Blob> {
    const response = await apiClient.get(
      apiConfig.endpoints.exports.portfolioSummaryReportExcel,
      {
        responseType: 'blob',
        headers: {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      }
    );

    this.validateExcelResponse(response);
    return response.data;
  },

  // Export benchmark rates to Excel
  // Uses Axios to ensure headers are accessible (CORS-safe)
  async exportBenchmarkRatesToExcel(): Promise<void> {
    // Priority 1: Get valuation date from frontend and construct filename
    let filename: string | undefined;
    
    try {
      const valuationDate = await launchConfigService.getValuationDate();
      if (valuationDate) {
        // Format: RPX Benchmark rates YYYY-MM-DD.xlsx
        filename = `RPX Benchmark rates ${valuationDate}.xlsx`;
        console.log('[ExportService] Using filename from valuation date:', filename);
      }
    } catch (error) {
      console.warn('[ExportService] Failed to get valuation date from frontend:', error);
    }

    // Get the file from server
    const response = await apiClient.get(
      apiConfig.endpoints.exports.benchmarkRatesExcel,
      {
        responseType: 'blob',
        headers: {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      }
    );

    this.validateExcelResponse(response);
    
    // Priority 2: Try x-filename header from server (fallback if valuation date failed)
    if (!filename && response.headers) {
      // Try all possible case variations of x-filename header
      const headerKeys = Object.keys(response.headers);
      const xFilenameKey = headerKeys.find(key => key.toLowerCase() === 'x-filename');
      
      if (xFilenameKey) {
        const headerValue = response.headers[xFilenameKey];
        if (headerValue) {
          if (typeof headerValue === 'string') {
            filename = headerValue.trim();
          } else if (Array.isArray(headerValue) && headerValue.length > 0) {
            filename = String(headerValue[0]).trim();
          } else {
            filename = String(headerValue).trim();
          }
        }
      }
      
      // Also try direct access with different cases (Axios may normalize differently)
      if (!filename) {
        const directValue = response.headers['x-filename'] || 
                           response.headers['X-Filename'] ||
                           response.headers['X-FILENAME'];
        if (directValue) {
          filename = typeof directValue === 'string' ? directValue.trim() : String(directValue).trim();
        }
      }
      
      if (filename) {
        console.log('[ExportService] Using filename from x-filename header:', filename);
      }
    }

    // Priority 3: Try content-disposition header as fallback
    if (!filename && response.headers) {
      const cdValue = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      if (cdValue) {
        const contentDisposition = typeof cdValue === 'string' ? cdValue : String(cdValue);
        
        // Try to extract filename from content-disposition
        let match = contentDisposition.match(/filename\*=UTF-8''([^;]+?)(?:;|$)/i);
        if (match && match[1]) {
          try {
            filename = decodeURIComponent(match[1]).trim();
          } catch (e) {
            console.warn('[ExportService] Failed to decode RFC 5987 filename:', e);
          }
        }
        
        if (!filename) {
          match = contentDisposition.match(/filename\s*=\s*["']([^"']+)["']/i);
          if (match && match[1]) {
            filename = match[1].trim();
          }
        }
        
        if (!filename) {
          match = contentDisposition.match(/filename\s*=\s*([^;]+?)(?:\s*;|$)/i);
          if (match && match[1]) {
            filename = match[1].trim();
          }
        }
        
        if (filename) {
          console.log('[ExportService] Using filename from content-disposition header:', filename);
        }
      }
    }
    
    // Priority 4: Use today's date as last resort to ensure file can be exported
    if (!filename) {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      filename = `RPX Benchmark rates ${today}.xlsx`;
      console.warn('[ExportService] Filename not found from any source, using fallback with today\'s date:', filename);
    }

    // Download file with filename (from server, content-disposition, or fallback)
    const downloadUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename.trim();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    console.log('[ExportService] File downloaded successfully with filename:', filename.trim());
  },

  // Enhanced error handling for Excel responses
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validateExcelResponse(response: { headers?: any }): void {
    const contentType = String(response.headers?.['content-type'] || '');
    const isValidExcel =
      contentType.includes('spreadsheetml.sheet') ||
      contentType.includes('octet-stream');

    if (!isValidExcel) {
      throw new Error('Invalid Excel format received from server');
    }

    // Check if response is actually an error message in JSON format
    if (contentType.includes('application/json')) {
      throw new Error('Server returned an error instead of Excel file');
    }
  },

  // Data Refresh Function (10-15 seconds expected duration) - Legacy
  async refreshData(): Promise<RefreshResponse> {
    const response = await apiClient.post<RefreshResponse>(
      apiConfig.endpoints.refresh.trigger,
      {}
    );

    // Store last refresh timestamp
    localStorage.setItem('lastDataRefresh', new Date().toISOString());

    return response.data;
  },

  // Refresh materialized views (NEW - ~1200x faster)
  async refreshMaterializedViews(): Promise<RefreshResponse> {
    const response = await apiClient.post<RefreshResponse>(
      apiConfig.endpoints.refresh.materializedViews,
      {}
    );

    // Store last refresh timestamp
    localStorage.setItem(
      'lastMaterializedViewsRefresh',
      new Date().toISOString()
    );

    return response.data;
  },

  // Refresh with custom dates
  async refreshWithCustomDates(
    valuationDate: string,
    settlementDate: string,
    updateConfig = false
  ): Promise<RefreshResponse> {
    const url = apiConfig.endpoints.refresh.withCustomDates(
      valuationDate,
      settlementDate,
      updateConfig
    );

    const response = await apiClient.post<RefreshResponse>(url, {});

    return response.data;
  },

  // Utility function to download blob as file
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // Get last refresh timestamp (legacy)
  getLastRefreshTime(): Date | null {
    const timestamp = localStorage.getItem('lastDataRefresh');
    return timestamp ? new Date(timestamp) : null;
  },

  // Get last materialized views refresh timestamp
  getLastMaterializedViewsRefreshTime(): Date | null {
    const timestamp = localStorage.getItem('lastMaterializedViewsRefresh');
    return timestamp ? new Date(timestamp) : null;
  },
};
